import { useEffect, useRef, useState } from 'react';
import { setup, createActor } from 'xstate';
import { createBrowserInspector } from '@statelyai/inspect';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';

import { usePartner } from 'components/app/routes/usePartner';
import { guards } from 'machines/config/guards';
import { restoreState } from 'state/reset-state/reset-state-actions';
import { checkMatchPath, stringToBool } from 'utils/misc';
import { pushHistory } from 'state/journey-routes/journey-routes-slice';
import { addQueryString, addHash } from 'machines/utils/misc';
import { useComputeGuards } from 'machines/hooks/useComputeGuards';
import { useHistoryNavigation } from 'hooks/useNavigation';
import { getMeta } from 'machines/utils/misc';
import { ReactRenderingError } from 'errors/ReactRenderingError';

const { VITE_ANY_PAGE_LANDING, VITE_XSTATE_DEBUG } = process.env;

const debug = stringToBool(VITE_XSTATE_DEBUG);

const { inspect } = debug
  ? /* istanbul ignore next -- @preserve */
    createBrowserInspector({
      autoStart: true
    })
  : {};

export const useJourneyMachine = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { stateMachineConfig } = usePartner();
  const [currentActor, setCurrentActor] = useState(null);
  const { pathname } = history.location;
  const anyPageLanding = stringToBool(VITE_ANY_PAGE_LANDING);
  let anyPageLandingInitialState;
  const computedGuards = useComputeGuards();
  const navigate = useHistoryNavigation();

  /* istanbul ignore else -- @preserve */
  if (anyPageLanding) {
    [anyPageLandingInitialState] =
      Object.entries(stateMachineConfig.states).find(([, value]) => {
        return checkMatchPath(pathname, value?.meta?.path);
      }) || [];
  }

  const machineRef = useRef(
    setup({
      guards
    }).createMachine({
      ...stateMachineConfig,
      initial: anyPageLandingInitialState ?? stateMachineConfig.initial
    })
  );

  const machine = machineRef.current;

  useEffect(() => {
    const {
      location: { search, hash, pathname }
    } = history;

    // initial path
    const fullPath = addHash(
      addQueryString(pathname, queryString.parse(search)),
      hash
    );
    dispatch(pushHistory(fullPath));

    /* istanbul ignore else -- @preserve */
    if (!currentActor) {
      const actor = createActor(machine, {
        inspect
      });

      setCurrentActor(actor);
      actor.start();
      actor.send({ type: 'UPDATE_GUARDS', guards: computedGuards });

      const isStateMatchingPath = checkMatchPath(
        pathname,
        getMeta(actor.getSnapshot()).path
      );

      // used only for development and testing. Normal usage should go trough all transitions
      // landing directly on states with "always" transition (redirects)
      if (!isStateMatchingPath) {
        navigate({
          path: getMeta(actor.getSnapshot()).path,
          replace: true
        });
      }

      const state = actor.getSnapshot();

      /* istanbul ignore next -- @preserve */
      if (state.status === 'error') {
        throw new ReactRenderingError({
          error: new Error(state.error)
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle browser back navigation
  useEffect(() => {
    const popstateHandler = () => {
      const {
        location: { state }
      } = history;

      dispatch(restoreState({ store: state?.store }));
      currentActor.stop();

      const newActor = createActor(machine, {
        inspect,
        snapshot: state?.snapshot
      });
      newActor.start();
      setCurrentActor(newActor);
    };

    window.addEventListener('popstate', popstateHandler);

    return () => {
      window.removeEventListener('popstate', popstateHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActor]);

  return {
    actor: currentActor,
    machine
  };
};
